'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'

export default function Post20Content() {
  return (
    <BlogPostLayout
      title="Microplastics and Fertility: What Every Couple Should Know"
      author="MicroPlastics Team"
      date="November 20, 2025"
      readTime="9 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="MicroPlastics app tracking exposure for fertility"
    >
      <BlogTLDR>
        Microplastics have been detected in <strong>human ovarian follicular
        fluid, testicular tissue, and every semen sample tested in two 2024
        studies.</strong> Higher BPA and phthalate exposure correlates with
        reduced sperm count, motility, and IVF success rates in cohort
        studies. The <strong>3-6 month preconception window</strong> is the
        best time for both partners to reduce exposure, sperm
        regenerate every 64-72 days; egg quality is also influenced by recent
        plasticiser exposure.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Microplastics detected in 100% of human semen samples in two 2024 studies.</>,
          <>Particles also found in ovarian follicular fluid and testicular tissue.</>,
          <>Higher BPA + phthalate levels correlate with lower sperm count, motility, and IVF embryo quality in cohort studies.</>,
          <>Sperm regenerate every 64-72 days, a 3-6 month reduction protocol can materially improve sperm quality.</>,
          <>The swaps that matter most for couples: filtered water in glass, no microwaving plastic, plastic-free personal care, cotton vs synthetic underwear for the male partner.</>,
          <>2026 (ESHRE): microplastics found in <strong>69%</strong> of follicular fluid and <strong>55%</strong> of seminal fluid samples; PTFE was the most common polymer (31%).</>,
          <>A negative correlation with fertilization rate was observed, but researchers stress current evidence does <em>not</em> show microplastics cause infertility.</>,
        ]}
      />

      {/* Introduction */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        A Global Fertility Crisis With a Plastic Connection
      </h2>
      <p>
        Something alarming is happening to human fertility around the world. Birth rates are plummeting in
        nearly every industrialized nation, and not just because couples are choosing to have fewer children.
        Scientific data increasingly suggests that biological fertility itself is in decline. Sperm counts
        among men in Western countries have dropped by more than 50% since the 1970s, miscarriage rates are
        climbing, and more couples than ever are turning to assisted reproductive technologies like IVF to
        conceive. While many factors contribute to these trends -- stress, diet, delayed parenthood, and
        lifestyle changes among them -- a growing body of research points to an overlooked culprit that is
        literally everywhere: microplastics.
      </p>
      <p>
        Microplastics are tiny plastic particles less than 5 millimeters in size that have infiltrated every
        corner of our environment. They are in the water we drink, the food we eat, the air we breathe, and
        the products we use on our bodies every single day. The average person ingests approximately 5 grams
        of microplastics per week. But what makes microplastics particularly dangerous for fertility is not
        just the plastic particles themselves -- it is the cocktail of endocrine-disrupting chemicals they
        carry into our bodies.
      </p>
      <p>
        In this article, we will walk through the latest research connecting microplastics to reproductive
        health problems in both men and women, explain the specific chemicals involved and how they interfere
        with conception, and lay out a practical plan for couples who are trying to conceive or planning to
        start a family in the future. The science is sobering, but the good news is that meaningful exposure
        reduction is absolutely possible once you know what to look for.
      </p>

      {/* Microplastics in Reproductive Tissue */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        2026 Update: Measured in Both Partners&apos; Reproductive Fluids
      </h2>
      <p>
        The most direct evidence yet was presented at the{' '}
        <strong>41st Annual Meeting of the European Society of Human Reproduction and
        Embryology (ESHRE)</strong>. Researchers analysed follicular fluid from 29 women and
        seminal fluid from 22 men -- the two fluids that matter most for both natural
        conception and IVF.
      </p>
      <p>
        Microplastics were found in <strong>69% of follicular fluid samples</strong> and{' '}
        <strong>55% of seminal fluid samples</strong>. The polymer breakdown was led by{' '}
        <strong>PTFE at 31%</strong> -- the non-stick coating -- followed by polypropylene
        (28%), PET (17%), polyamide (14%), polyethylene (10%), polyurethane (10%) and
        polystyrene (7%).
      </p>
      <p>
        The finding that will get quoted is a{' '}
        <strong>negative correlation between total microplastics in follicular fluid and
        fertilization rate</strong>. It deserves careful handling. These are small samples,
        the correlation is not a controlled experiment, and the researchers were explicit on
        the bottom line: <strong>current evidence does not show that microplastics cause
        infertility.</strong>
      </p>
      <p>
        The honest summary is that exposure reaches the tissues where conception happens, in
        both partners, and that there is a preliminary signal worth investigating -- not that
        microplastics have been shown to make people infertile. Anyone struggling to conceive
        should be working with a clinician on the many factors with established evidence
        behind them, and treating plastic reduction as a low-cost addition rather than a
        substitute.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Microplastics Are Accumulating in Reproductive Tissue
      </h2>
      <p>
        For years, the presence of microplastics in the human body was suspected but not definitively proven
        in reproductive organs. That changed dramatically with a series of landmark studies that found
        microplastics accumulating in the very tissues responsible for creating new life.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Microplastics Found in Testicular Tissue</h3>
      <p>
        In 2023, researchers at the University of New Mexico published a groundbreaking study that analyzed
        testicular tissue from both humans and dogs. The results were staggering. Every single sample tested
        positive for microplastics. Human testicular tissue contained an average of 329 micrograms of
        microplastics per gram of tissue -- nearly three times the concentration found in the dog samples.
        The most common plastic types detected were polyethylene (the plastic used in bags and bottles) and
        PVC. The researchers noted that the concentration of microplastics in testes was significantly higher
        than levels found in other organs like the liver or kidneys, suggesting that reproductive tissue may
        be particularly vulnerable to plastic accumulation.
      </p>
      <p>
        What made this study especially concerning was the correlation the researchers observed between higher
        microplastic concentrations and lower sperm counts in the tissue samples. While the study was conducted
        on postmortem tissue and could not establish direct causation in living subjects, the association between
        plastic burden and reduced sperm production was statistically significant and consistent across samples.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Microplastics Found in the Human Placenta</h3>
      <p>
        In 2020, researchers at Fatebenefratelli Hospital in Rome made headlines when they published the first
        study documenting the presence of microplastics in human placentas. The team examined six placentas
        from women who had experienced otherwise normal pregnancies and deliveries. In four of the six
        placentas, they found microplastic particles -- twelve fragments in total, ranging in size from 5 to
        10 micrometers. The particles were found on both the fetal and maternal sides of the placenta, as well
        as in the chorioamniotic membranes that surround the fetus.
      </p>
      <p>
        The implications of this discovery are profound. The placenta is supposed to act as a protective
        barrier between mother and baby, filtering out harmful substances while allowing nutrients to pass
        through. The fact that microplastics are breaching this barrier means that developing fetuses are being
        exposed to plastic particles and the chemicals they carry during the most critical stages of
        development. Subsequent studies have confirmed and expanded on these findings, with researchers
        detecting microplastics in amniotic fluid, umbilical cord blood, and even in the first stool of
        newborn babies.
      </p>

      {/* Sperm Count Decline */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Sperm Count Crisis: A Meta-Analysis of Decline
      </h2>
      <p>
        The global decline in sperm counts is one of the most well-documented reproductive health trends of
        the past half century. A landmark meta-analysis published in the journal Human Reproduction Update
        examined data from 223 studies involving nearly 58,000 men across 53 countries between 1973 and 2018.
        The findings were alarming: sperm concentration among men in Western countries declined by 51.6%
        over the study period, falling from an average of 101 million sperm per milliliter to just 49 million.
        Total sperm count dropped even more dramatically, by 62.3%.
      </p>
      <p>
        Perhaps most concerning, the rate of decline appears to be accelerating. Data from the most recent
        decades showed a steeper downward trajectory than earlier periods, suggesting that whatever is driving
        the decline is intensifying rather than stabilizing. The study authors specifically identified
        environmental chemical exposures, including endocrine-disrupting chemicals commonly associated with
        plastics, as likely contributing factors.
      </p>
      <p>
        A follow-up analysis extended the dataset globally and found that the decline is no longer limited to
        Western nations. Men in South America, Asia, and Africa are now showing similar downward trends,
        correlating with the global spread of plastic products and the chemicals that accompany them. If
        current trends continue, some researchers project that median sperm counts could approach zero in
        certain populations by the 2040s -- a timeline that has earned this trend the informal name
        &quot;Spermageddon&quot; in scientific circles.
      </p>

      {/* Endocrine Disrupting Chemicals */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Chemical Hitchhikers: Endocrine Disruptors in Microplastics
      </h2>
      <p>
        Microplastics are not just inert pieces of plastic floating through your body. They act as carriers
        for a range of toxic chemicals that were either added during manufacturing or absorbed from the
        environment. Many of these chemicals are classified as endocrine-disrupting chemicals, or EDCs --
        substances that interfere with the body&apos;s hormone systems. For fertility, the
        endocrine system is everything. Hormones like estrogen, testosterone, progesterone, and follicle-
        stimulating hormone orchestrate every step of the reproductive process, from egg maturation and
        ovulation to sperm production and embryo implantation.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">BPA (Bisphenol A)</h3>
      <p>
        BPA is one of the most extensively studied endocrine disruptors. It is used in the production of
        polycarbonate plastics and epoxy resins, and it is found in food can linings, receipt paper, water
        bottles, and food storage containers. BPA mimics estrogen in the body, binding to estrogen receptors
        and disrupting normal hormonal signaling. In men, BPA exposure has been linked to reduced sperm count,
        decreased sperm motility, and abnormal sperm morphology. In women, BPA has been associated with
        disrupted ovarian function, reduced egg quality, and lower success rates in IVF treatments. Multiple
        studies have found that women with higher BPA levels in their blood had significantly fewer eggs
        retrieved during IVF cycles and lower rates of successful embryo implantation.
      </p>
      <p>
        While many products now carry &quot;BPA-free&quot; labels, this does not necessarily mean they are
        safe. Many manufacturers have replaced BPA with chemically similar compounds like BPS and BPF, which
        early research suggests may be equally or even more disruptive to the endocrine system.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Phthalates</h3>
      <p>
        Phthalates are a family of chemicals used to make plastics soft and flexible. They are found in food
        packaging, personal care products, fragranced products, vinyl flooring, shower curtains, and children&apos;s
        toys. Unlike BPA, which mimics estrogen, phthalates primarily interfere with testosterone and other
        androgens. In men, phthalate exposure has been strongly correlated with reduced testosterone levels,
        lower sperm counts, decreased sperm motility, and increased DNA damage in sperm cells. In women,
        phthalate exposure has been linked to endometriosis, disrupted ovarian function, and reduced fertility.
      </p>
      <p>
        One particularly alarming aspect of phthalate exposure is its transgenerational effects. Animal
        studies have demonstrated that phthalate exposure in pregnant mothers can reduce fertility in their
        offspring and even in their grandchildren, suggesting that the reproductive damage may be epigenetic
        and heritable. A study published in Environmental Health Perspectives found that pregnant women with
        higher phthalate levels were more likely to give birth to sons with reduced anogenital distance -- a
        marker of diminished androgen exposure in utero that has been associated with lower sperm counts in
        adulthood.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Other Chemicals of Concern</h3>
      <p>
        Beyond BPA and phthalates, microplastics carry a range of additional chemicals that affect
        reproductive health. These include PFAS (per- and polyfluoroalkyl substances, also known as
        &quot;forever chemicals&quot;), flame retardants like PBDEs, heavy metals like lead and cadmium that
        adsorb onto plastic surfaces, and UV stabilizers used in plastic manufacturing. Each of these has
        independently documented effects on fertility, and the combined exposure from multiple chemicals
        simultaneously -- known as the &quot;cocktail effect&quot; -- may be significantly more harmful than
        any single chemical alone. For a deeper dive into the health implications of these chemicals, read our
        guide on{' '}
        <Link href="/blog/microplastics-health-effects" className="text-[#1E6B47] hover:underline font-medium">
          microplastics and health effects
        </Link>.
      </p>

      {/* Female Reproductive Health */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Effects on Female Reproductive Health
      </h2>
      <p>
        While much of the early research on microplastics and fertility focused on sperm quality, a growing
        body of evidence now demonstrates significant effects on female reproductive health as well. The
        female reproductive system is exquisitely sensitive to hormonal disruption, and the endocrine-
        disrupting chemicals carried by microplastics can interfere with nearly every stage of the
        reproductive process.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Ovarian Function and Egg Quality</h3>
      <p>
        Studies have shown that exposure to microplastics and associated EDCs can impair ovarian function
        in several ways. Laboratory research has demonstrated that microplastic particles can penetrate
        ovarian tissue, triggering inflammatory responses that damage developing follicles. EDCs like BPA
        and phthalates disrupt the hormonal signals that govern egg maturation, leading to fewer viable
        eggs and premature ovarian aging. Research published in Science of the Total Environment found that
        mice exposed to polystyrene microplastics showed significant reductions in the number of mature
        follicles and lower levels of anti-Mullerian hormone (AMH), a key marker of ovarian reserve.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">IVF Outcomes and Microplastic Exposure</h3>
      <p>
        For couples already undergoing fertility treatment, microplastic exposure may be undermining their
        chances of success. Several studies have examined the relationship between environmental chemical
        exposure and IVF outcomes. Women with higher urinary concentrations of BPA had significantly fewer
        oocytes retrieved, lower fertilization rates, and reduced embryo quality. One study from Harvard
        T.H. Chan School of Public Health found that women in the highest quartile of BPA exposure had a
        27% lower probability of successful implantation compared to women in the lowest quartile. Similar
        associations have been found for phthalate exposure, with higher levels correlating with reduced
        success rates across multiple stages of the IVF process.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Endometriosis and Uterine Health</h3>
      <p>
        Endometriosis, a painful condition in which tissue similar to the uterine lining grows outside the
        uterus, affects roughly 10% of reproductive-age women and is a leading cause of infertility.
        Research has established a connection between phthalate exposure and increased risk of endometriosis.
        The estrogenic activity of BPA and similar compounds may also promote the growth of endometrial
        tissue in abnormal locations. Women with endometriosis have been found to have higher concentrations
        of certain phthalates in their blood and peritoneal fluid compared to women without the condition,
        suggesting a potential causal relationship that researchers continue to investigate.
      </p>

      {/* What Couples Should Do */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What Couples Trying to Conceive Should Do
      </h2>
      <p>
        The research can feel overwhelming, but the practical steps to reduce your microplastic exposure
        are straightforward and effective. If you are actively trying to conceive or planning to start a
        family in the coming months or years, here are the most impactful changes you can make, organized
        by priority.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">1. Switch to Glass, Stainless Steel, and Ceramic</h3>
      <p>
        One of the most effective steps you can take is to replace plastic food and beverage
        containers with glass, stainless steel, or ceramic alternatives. This eliminates a major source
        of both microplastic particle ingestion and chemical leaching. Use glass food storage containers
        instead of plastic ones. Drink from stainless steel or glass water bottles. Replace plastic cutting
        boards with wood or bamboo. Store leftovers in glass containers with silicone lids rather than
        plastic wrap or plastic containers. This one change alone can dramatically reduce your daily
        exposure to BPA, phthalates, and microplastic particles.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">2. Filter Your Drinking Water</h3>
      <p>
        Tap water and bottled water both contain microplastics, but the concentration in bottled water
        can be dramatically higher. A study published in Proceedings of the National Academy of Sciences
        found an average of 240,000 detectable plastic fragments per liter of bottled water -- up to 100
        times more than previous estimates that only measured larger particles. Invest in a high-quality
        water filter that is certified to remove microplastics. Reverse osmosis systems are the most
        effective, removing up to 99.9% of microplastic particles. Activated carbon filters and
        nanofiltration systems also provide meaningful reduction. Avoid drinking directly from single-use
        plastic bottles whenever possible.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">3. Never Heat Food in Plastic</h3>
      <p>
        Heat dramatically accelerates the release of both microplastic particles and chemical additives
        from plastic containers. Microwaving food in plastic containers, pouring hot liquids into plastic
        cups, and running plastic items through the dishwasher all increase microplastic contamination
        significantly. A study from the University of Nebraska-Lincoln found that microwaving plastic baby
        bottles and food containers released up to 4.2 million microplastic particles per square centimeter.
        Always transfer food to glass or ceramic containers before heating. Let hot beverages cool slightly
        before pouring them into any container, and hand-wash plastic items if you must continue using them.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">4. Choose Natural Personal Care Products</h3>
      <p>
        Personal care products are a significant and often overlooked source of microplastic and EDC exposure.
        Shampoos, conditioners, body washes, lotions, deodorants, and cosmetics frequently contain synthetic
        polymer ingredients and are packaged in plastic containers that leach chemicals through daily use.
        Switch to products with short, recognizable ingredient lists. Choose brands that use glass or aluminum
        packaging. Avoid products containing ingredients that start with &quot;poly-&quot; or include the word
        &quot;copolymer.&quot; Be especially cautious with products labeled as &quot;fragranced&quot; or
        containing &quot;parfum,&quot; as these undisclosed fragrance blends frequently contain phthalates.
        For a complete guide to reducing exposure from everyday products, see our article on{' '}
        <Link href="/how-to-avoid-microplastics" className="text-[#1E6B47] hover:underline font-medium">
          how to avoid microplastics
        </Link>.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">5. Reduce Exposure in Your Home Environment</h3>
      <p>
        Microplastics are present in household dust, and indoor air can contain higher concentrations of
        plastic particles than outdoor air. Synthetic carpets, upholstered furniture, polyester clothing,
        and plastic home goods all shed microfibers and particles that become airborne and are inhaled or
        ingested. Vacuum frequently using a vacuum with a HEPA filter. Wet-mop hard floors rather than
        sweeping, which can stir particles into the air. Open windows regularly to ventilate indoor spaces.
        When possible, choose natural fiber furnishings -- cotton, wool, linen, or silk -- over synthetic
        alternatives. Wash synthetic clothing in a microfiber-catching laundry bag to prevent microfibers
        from entering your water supply and home environment.
      </p>

      {/* Preconception Detox Timeline */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Preconception Detox Timeline: When to Start
      </h2>
      <p>
        If you are planning to conceive, the ideal time to begin reducing your microplastic exposure is
        at least three to six months before you start trying. Here is why that timeline matters and what
        to focus on during each phase.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Six Months Before Trying to Conceive</h3>
      <p>
        Sperm take approximately 74 days to fully mature, meaning that the sperm used in conception reflect
        the chemical environment from roughly two and a half months earlier. For women, egg maturation begins
        even earlier, with the final stages of follicle development spanning about 90 days before ovulation.
        Starting your exposure reduction six months out gives your body time to clear existing chemical
        burdens and allows new reproductive cells to develop in a cleaner environment.
      </p>
      <p>
        During this phase, focus on the biggest exposure sources first: replace plastic food and water
        containers with glass or steel, install a water filter, and audit your personal care products for
        synthetic polymer ingredients. These changes alone can reduce your daily microplastic and EDC intake
        by 50% or more.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Three Months Before Trying to Conceive</h3>
      <p>
        By this point, your major exposure sources should already be addressed. Now focus on the secondary
        sources: household dust reduction, switching to natural fiber clothing and bedding, addressing
        workplace exposure if relevant, and fine-tuning your diet to minimize food-related microplastic
        intake. Choose fresh, unpackaged foods over processed foods in plastic packaging. Buy from
        farmers markets when possible. Avoid canned foods with plastic linings. Cook at home using
        stainless steel or cast iron cookware rather than nonstick coated pans, which can release
        microplastic particles when the coating degrades.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">One Month Before and During Conception Attempts</h3>
      <p>
        In the final month, maintain all the changes you have already made and add targeted nutritional
        support. Antioxidants like vitamin C, vitamin E, selenium, and zinc have been shown to help
        protect reproductive cells from oxidative stress caused by environmental chemicals. Folate (not
        just folic acid) supports healthy DNA replication during the earliest stages of embryonic
        development. Both partners should be taking a high-quality prenatal or fertility supplement that
        includes these nutrients. Continue to monitor and minimize your exposure using the strategies
        above, and use the MicroPlastics app to scan any new products that enter your home.
      </p>

      {/* How the App Helps */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How the MicroPlastics App Helps Couples Protect Their Fertility
      </h2>
      <p>
        Reducing microplastic exposure sounds simple in theory, but the reality is that plastic is woven
        into nearly every product we touch. That is exactly why we built the MicroPlastics app -- to make
        it fast and easy to identify which products are contributing to your exposure and find safer
        alternatives.
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Instant Product Scanning:</strong> Point your phone camera at any product barcode and
          get an immediate safety score from A (safest) to F (highest concern). The app uses AI to analyze
          ingredients, packaging materials, and known contamination data, so you do not need to memorize
          lists of chemical names or try to decode ingredient labels yourself.
        </li>
        <li>
          <strong>Exposure Tracking Dashboard:</strong> Every product you scan is logged, and the app
          builds a comprehensive picture of your total microplastic exposure over time. For couples
          following a preconception detox timeline, this feature lets you see exactly how much your
          exposure decreases as you swap out products -- providing concrete evidence that your changes
          are making a real difference.
        </li>
        <li>
          <strong>Safer Alternative Recommendations:</strong> When the app identifies a product with
          concerning microplastic levels, it immediately suggests safer alternatives in the same category.
          This eliminates the guesswork of finding replacement products and makes the transition to a
          lower-exposure lifestyle as frictionless as possible.
        </li>
        <li>
          <strong>Category-Specific Insights:</strong> The app breaks down your exposure by product
          category -- food, personal care, cleaning products, baby products, and more -- so you can see
          which areas of your life are contributing the most and prioritize your changes accordingly.
        </li>
        <li>
          <strong>Household Scanning:</strong> Scan every product in your kitchen, bathroom, and medicine
          cabinet to get a complete picture of your household&apos;s microplastic exposure. Many couples
          report that a single scanning session reveals surprising sources of exposure they never would
          have identified on their own.
        </li>
      </ul>

      {/* Conclusion */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Bottom Line: Knowledge Is Your Best Protection
      </h2>
      <p>
        The connection between microplastics and fertility is no longer a theoretical concern. Peer-reviewed
        research has documented microplastics in testicular tissue and placentas, quantified the chemicals
        they carry into our bodies, and established clear associations between exposure levels and
        reproductive outcomes. Sperm counts are declining globally, female reproductive health is being
        impacted, and the chemicals riding on microplastic particles are among the most potent endocrine
        disruptors known to science.
      </p>
      <p>
        But this is not a story without hope. The same research that identifies the problem also points
        clearly toward solutions. Reducing your exposure to microplastics and the endocrine-disrupting
        chemicals they carry is achievable with practical, everyday changes: switching away from plastic
        food containers, filtering your water, choosing natural personal care products, and being mindful
        of the materials in your home. Starting these changes three to six months before conception gives
        your body time to benefit from a cleaner chemical environment.
      </p>
      <p>
        Whether you are actively trying to conceive right now or simply thinking about starting a family
        someday, the time to act is now. Every day of reduced exposure is a step toward healthier
        reproductive cells, a healthier pregnancy, and a healthier start for your future child.
      </p>
      <p className="font-semibold text-[#1E6B47]">
        Download the MicroPlastics app today and start scanning the products in your home. Understanding
        your exposure is the first step toward protecting your fertility and your family&apos;s future.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Baby/kid product material, glass, stainless, silicone, polypropylene, PPSU.</>,
          <>Packaging type, jar vs pouch vs multi-layer plastic.</>,
          <>Brand and product line, clean certifications flagged.</>,
          <>Use-context flags you log, sterilization heat, dishwasher cycles, age.</>,
          <>Cited published research behind each 0–100 score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan baby gear and pregnancy products before buying"
        sub="Bottles, sippy cups, baby food pouches, cosmetics. The app weighs material + brand + condition and suggests cleaner-packaged alternatives."
        buttonLabel="Scan baby gear in the app"
      />


      <BlogFAQ
        items={[
          {
            question: 'Can microplastics affect fertility?',
            answer:
              'Mechanistic evidence suggests yes. Microplastics have been detected in human ovarian follicular fluid, testicular tissue, and every semen sample tested in two 2024 studies. Higher BPA and phthalate exposure correlates with reduced sperm count, motility, and IVF outcomes in cohort studies. Causation in humans is not yet proven by randomized trial, and cannot be ethically tested as such.',
          },
          {
            question: 'How long before trying to conceive should we reduce microplastic exposure?',
            answer:
              'At least 3 months, ideally 6. Sperm fully regenerate every 64-72 days, so changes the male partner makes today affect the sperm he produces 2-3 months later. Egg quality is also influenced by recent (3-6 month) plasticiser exposure. Both partners benefit.',
          },
          {
            question: 'What is the most important fertility-related microplastic source to address?',
            answer:
              'For most couples: filtered water (vs bottled), stopping microwaving plastic, and switching personal care to plastic-free formulations. For the male partner specifically: switching from synthetic to cotton/wool underwear, since polyester releases microfibres directly onto reproductive skin.',
          },
          {
            question: 'Does plastic exposure affect IVF success?',
            answer:
              'Cohort studies link higher BPA and phthalate levels in IVF patients to fewer retrieved eggs, fewer good-quality embryos, and lower live birth rates. The strongest evidence is for phthalate exposure during the stimulation phase. A 3-month pre-cycle plasticiser reduction protocol is reasonable. See the dedicated IVF guide for details.',
          },
          {
            question: 'Are male and female fertility affected differently?',
            answer:
              'Both are affected through related but distinct pathways. Male fertility shows clearer dose-response relationships with phthalates and BPA on sperm parameters. Female fertility shows stronger signals on egg quality and implantation. Both improve with the same reduction strategy.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Presented at the 41st Annual Meeting of ESHRE',
            year: 2025,
            title: 'Microplastics detected in human follicular and seminal fluid',
            publisher: 'European Society of Human Reproduction and Embryology',
            url: 'https://www.eurekalert.org/news-releases/1088676',
          },
          {
            authors: 'Research collaboration',
            year: 2025,
            title: 'First evidence of microplastics in human ovarian follicular fluid: an emerging threat to female fertility',
            publisher: 'Ecotoxicology and Environmental Safety',
            url: 'https://www.sciencedirect.com/science/article/pii/S0147651325002040',
          },
          {
            authors: 'Garcia MA, Liu R, Nihart A, et al.',
            year: 2024,
            title: 'Quantitation and identification of microplastics in human placental specimens',
            publisher: 'Toxicological Sciences',
            url: 'https://academic.oup.com/toxsci/article/199/1/81/7609299',
          },
          {
            authors: 'Li D, Shi Y, Yang L, et al.',
            year: 2020,
            title: 'Microplastic release from polypropylene infant feeding bottles',
            publisher: 'Nature Food',
            url: 'https://www.nature.com/articles/s43016-020-00171-y',
          },
          {
            authors: 'Ragusa A, Notarstefano V, Svelato A, et al.',
            year: 2022,
            title: 'Raman Microspectroscopy Detection and Characterisation of Microplastics in Human Breastmilk',
            publisher: 'Polymers',
            url: 'https://www.mdpi.com/2073-4360/14/13/2700',
          },
          {
            authors: 'ACOG Committee Opinion 832',
            year: 2021,
            title: 'Reducing prenatal exposure to toxic environmental agents',
            publisher: 'American College of Obstetricians and Gynecologists',
            url: 'https://www.acog.org/clinical/clinical-guidance/committee-opinion/articles/2021/07/reducing-prenatal-exposure-to-toxic-environmental-agents',
          },
          {
            authors: 'Leslie HA, van Velzen MJM, Brandsma SH, et al.',
            year: 2022,
            title: 'Discovery and quantification of plastic particle pollution in human blood',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412022001258',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
